'use client';

import { useState, useTransition } from 'react';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '@bridge/utils';

export interface UseServerActionOptions<TData = unknown, TError = unknown> {
  /** Success message to show in toast (if provided) */
  successMessage?: string;
  /** Error message to show in toast (if provided, otherwise uses error.message) */
  errorMessage?: string;
  /** Callback to execute on success */
  onSuccess?: (data: TData) => void | Promise<void>;
  /** Callback to execute on error */
  onError?: (error: TError) => void | Promise<void>;
  /**
   * Callback to execute when the mutation is gated behind a sensitive-action approval rule
   * (HTTP 202). The action has NOT been applied; the approval modal opens automatically. The
   * success/error paths are skipped and `execute` rejects, so this is the place to add behavior
   * like closing a sheet. Only fires when the hook is created with an `isPendingApproval` predicate.
   */
  onPendingApproval?: () => void;
  /** Whether to show success toast (default: true if successMessage provided) */
  showSuccessToast?: boolean;
  /** Whether to show error toast (default: true) */
  showErrorToast?: boolean;
  /**
   * When the error is intercepted (see `isInterceptedError`), hand the interceptor a thunk that
   * re-runs this call with the same params, so it can retry once the blocking condition clears
   * (e.g. after a business-justification grant). Off by default: interception normally just surfaces
   * a recovery UI and the user re-triggers the action themselves.
   */
  retryAfterInterception?: boolean;
}

interface CreateServerActionHookConfig {
  /**
   * Identifies a thrown pending-approval signal (a 202 sensitive-action gate). When it matches,
   * the hook skips the error toast and `onError`, invokes `onPendingApproval`, and rejects —
   * so an expected approval gate is never surfaced as a failure.
   */
  isPendingApproval?: (error: unknown) => boolean;
  /**
   * Identifies a thrown 401. When it matches, the hook skips the error toast (the app is
   * already logging out) but still runs `onError` and rejects.
   */
  isUnauthorized?: (error: unknown) => boolean;
  /**
   * Identifies an error the app handles globally instead of toasting (e.g. a
   * business-justification 403 that opens a recovery modal). When it matches,
   * the hook skips the error toast and the caller-visible `error` state (the
   * interceptor owns the UX), invokes `onInterceptedError`, still runs
   * `onError`, and rejects.
   */
  isInterceptedError?: (error: unknown) => boolean;
  /**
   * Handles an intercepted error. `retry` re-runs the call with the same params; it is only
   * provided when the call opted in via `retryAfterInterception`, so an interceptor can wire it to
   * a recovery action's success (and is `undefined` otherwise).
   */
  onInterceptedError?: (error: unknown, retry?: () => void) => void;
}

export interface UseServerActionReturn<TData = unknown, TError = unknown> {
  /** Execute the server action with the given parameters */
  execute: (params?: unknown[]) => Promise<TData>;
  /** Whether the action is currently pending */
  isPending: boolean;
  /** The last successful result, held in state (persists until the next success or `reset`). */
  data: TData | undefined;
  /** The last error, held in state (cleared on the next success or `reset`). */
  error: TError | undefined;
  /** Clear `data` and `error` back to undefined. */
  reset: () => void;
}

/**
 * Factory to create a useServerAction hook with app-specific callServerAction.
 * This allows sharing the hook logic while injecting app-specific auth handling.
 *
 * @example
 * // In your app:
 * const baseUseServerAction = createServerActionHook(callServerAction);
 * export default baseUseServerAction;
 *
 * // Then use it:
 * const { execute, isPending } = useServerAction('myAction', {
 *   successMessage: 'Success!',
 *   onSuccess: (data) => console.log(data),
 * });
 */
export function createServerActionHook<TActionPath extends string = string, TErrorType = unknown>(
  callServerAction: <T>(action: TActionPath, params: unknown[]) => Promise<T>,
  config: CreateServerActionHookConfig = {}
) {
  const { isPendingApproval, isUnauthorized, isInterceptedError, onInterceptedError } = config;

  return function useServerAction<TData = unknown>(
    action: TActionPath,
    options: UseServerActionOptions<TData, TErrorType> = {}
  ): UseServerActionReturn<TData, TErrorType> {
    const [isPending, startTransition] = useTransition();
    const [data, setData] = useState<TData>();
    const [error, setError] = useState<TErrorType>();

    const {
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      onPendingApproval,
      showSuccessToast = !!successMessage,
      showErrorToast = true,
      retryAfterInterception = false,
    } = options;

    const execute = async (params: unknown[] = []): Promise<TData> => {
      return new Promise((resolve, reject) => {
        startTransition(async () => {
          try {
            const result = await callServerAction<TData>(action, params);
            setData(result);
            setError(undefined);

            if (showSuccessToast && successMessage) {
              toast.success(successMessage);
            }

            if (onSuccess) {
              await onSuccess(result);
            }

            resolve(result);
          } catch (error) {
            // A gated mutation (202) is an expected outcome, not a failure: the approval modal
            // already opened. Skip the error toast/onError and reject so success code doesn't run.
            if (isPendingApproval?.(error)) {
              onPendingApproval?.();
              reject(error);
              return;
            }

            const typedError = error as TErrorType;

            // An intercepted error is handled by the app's interceptor (recovery UI), so it
            // never reaches the caller-visible `error` state or the toast.
            if (isInterceptedError?.(error)) {
              // The retry thunk is fire-and-forget for the interceptor, so swallow its rejection
              // here: a failed retry re-surfaces through this same handler (toast or recovery UI).
              const retry = retryAfterInterception
                ? () => {
                    void execute(params).catch(() => undefined);
                  }
                : undefined;
              onInterceptedError?.(error, retry);
              if (onError) {
                await onError(typedError);
              }
              reject(error);
              return;
            }

            setError(typedError);

            // A 401 means the session is already dead and the app is logging out. Toasting
            // the raw API message ("Bad Session Token") would flash for the duration of the
            // logout redirect and tells the user nothing actionable.
            if (isUnauthorized?.(error)) {
              if (onError) {
                await onError(typedError);
              }
              reject(error);
              return;
            }

            if (showErrorToast) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const message = errorMessage || getErrorMessage(error as any) || 'An unexpected error occurred.';
              toast.error(message);
            }

            if (onError) {
              await onError(typedError);
            }

            reject(error);
          }
        });
      });
    };

    const reset = () => {
      setData(undefined);
      setError(undefined);
    };

    return {
      execute,
      isPending,
      data,
      error,
      reset,
    };
  };
}
